// Function to normalize a single question
const normalizeQuestion = question => ({
    id: question.id,
    name: question.name,
    content: question.text,
    gradingReferenceContent: question.grading_reference_content,
    order: question.order,
    sectionId: question.section,
    type: question.type,
    categories: question.patterns.map(
        ({ id, color_id, type, name, description, feedback, grade, conceptual_labels, attributes }) => ({
            id,
            colorId: color_id,
            type,
            name,
            description,
            feedback,
            grade,
            attributes,
            conceptualLabels: conceptual_labels,
        }),
    ),
    wordsLimit: question.word_limit,
    weight: question.weight,
    isGlobal: question.is_global,
    dependsOnQuestionIds: question.depends_on,
    processing: {
        progress: question.processing_progress,
        message: question.processing_msg,
        status: question.processing_status,
    },
    lastTrained: question.last_trained,
    parentId: question.parent_id,
    isGlobal: question.is_global,
})

// Function to build a map of normalized questions by ID
const buildQuestionMap = response => {
    return response.reduce((map, question) => {
        map[question.id] = normalizeQuestion(question)
        return map
    }, {})
}

// Function to group child questions under their parent
const assignChildrenToParents = (questionMap, response) => {
    response.forEach(({ id, parent_id }) => {
        if (parent_id && questionMap[parent_id]) {
            const parent = questionMap[parent_id]
            parent.children = parent.children || []
            parent.children.push(questionMap[id])
        }
    })
}

export const getQuestionById = (questions, id) => {
    const question = questions.find(question => question.id === id)
    if (question) return question
    return questions.find(question => question.children?.find(child => child.id === id))
}

// Function to preserve the original order and exclude unnecessary "children" properties
const buildOrderedHierarchy = (response, questionMap) => {
    return response
        .map(({ id, parent_id }) => {
            if (parent_id) return null // Skip child questions as they are already nested
            const question = questionMap[id]
            if (question.children && question.children.length === 0) {
                delete question.children // Remove empty children array for non-parent questions
            }
            return question
        })
        .filter(Boolean) // Remove skipped child questions
}

// Main function
export const normalizeFetchQuestionsResponse = response => {
    const questionMap = buildQuestionMap(response) // Normalize all questions
    assignChildrenToParents(questionMap, response) // Nest child questions under parents
    return buildOrderedHierarchy(response, questionMap) // Retain order and clean structure
}

export const normalizeFetchQuestionMetadataResponse = response => {
    const normalizedHighlights = {}
    for (const [answerId, categories] of Object.entries(response.highlights)) {
        let categoryHighlights = {}
        for (const [categoryId, highlights] of Object.entries(categories)) {
            categoryHighlights[categoryId] = highlights.masked_ranges.map((range, index) => ({
                start: range[0],
                end: range[1],
                importance: highlights.relative_importances[index],
            }))
        }
        normalizedHighlights[answerId] = categoryHighlights
    }

    return {
        highlights: normalizedHighlights,
    }
}

export const normalizeFetchQuestionChangesHistoryResponse = response =>
    response.map(change => ({
        field: change.updated_field,
        oldValue: change.old_value,
        newValue: change.new_value,
        createdAt: change.created_at,
    }))
